import React, { useState, useEffect } from "react";
import { Badge, Tooltip, Space, Typography, Button } from "antd";
import {
  ClockCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import "./TrialCountdownBadge.css";

const { Text } = Typography;

/**
 * Badge affichant le décompte des jours d'essai restants
 * Adapte sa couleur et son style selon l'urgence
 */
const TrialCountdownBadge = ({
  className = "",
  size = "default",
  showLabel = true,
  onUpgradeClick = null,
  centered = false,
}) => {
  const { user } = useAuth();
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Calculer les jours restants
  useEffect(() => {
    const calculateDaysRemaining = () => {
      // PROTECTION CRITIQUE: Si pas d'utilisateur ou pas d'entreprise
      if (!user || !user.company) {
        setDaysRemaining(null);
        setIsLoading(false);
        return;
      }

      // Si les informations d'essai sont directement dans user.trial
      if (user?.trial) {
        // CRITIQUE: Vérifier que daysRemaining n'est pas null/undefined
        const trialDays = user.trial.daysRemaining;
        if (trialDays === null || trialDays === undefined || isNaN(trialDays)) {
          setDaysRemaining(null); // Masquer le badge si pas de jours valides
        } else {
          setDaysRemaining(trialDays);
        }
        setIsLoading(false);
        return;
      }

      // VÉRIFIER SI L'ENTREPRISE A UN PLAN PAYANT ACTIF
      const company = user?.company;
      if (company) {
        // Si l'entreprise a un plan de souscription payant
        if (company.subscription_plan_id) {
          setDaysRemaining(null); // Masquer le badge
          setIsLoading(false);
          return;
        }

        // Si l'entreprise a une licence valide (non expirée)
        if (company.licence_expire_on) {
          const licenceExpiry = new Date(company.licence_expire_on);
          const today = new Date();

          if (licenceExpiry > today) {
            setDaysRemaining(null); // Masquer le badge
            setIsLoading(false);
            return;
          }
        }

        // Si l'entreprise a un statut actif et n'est plus en trial
        if (company.status === "active" && !company.trial_ends_at) {
          setDaysRemaining(null); // Masquer le badge
          setIsLoading(false);
          return;
        }
      }
      // Fallback : calculer à partir de user.company.trial_ends_at
      if (!user?.company?.trial_ends_at) {
        setDaysRemaining(null);
        setIsLoading(false);
        return;
      }

      const trialEndDate = new Date(user.company.trial_ends_at);
      const today = new Date();

      // PROTECTION: Vérifier que la date est valide
      if (isNaN(trialEndDate.getTime())) {
        setDaysRemaining(null);
        setIsLoading(false);
        return;
      }

      const diffTime = trialEndDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const calculatedDays = Math.max(0, diffDays);

      setDaysRemaining(calculatedDays);
      setIsLoading(false);
    };

    calculateDaysRemaining();

    // Mettre à jour le décompte toutes les heures
    const interval = setInterval(calculateDaysRemaining, 1000 * 60 * 60);

    return () => clearInterval(interval);
  }, [
    user,
    user?.company,
    user?.company?.subscription_plan_id,
    user?.company?.licence_expire_on,
    user?.company?.status,
    user?.company?.trial_ends_at,
  ]);

  // Ne rien afficher si :
  // - Données en cours de chargement
  // - Pas de période d'essai définie
  // - Utilisateur SuperAdmin
  // - daysRemaining est invalide (null, undefined, NaN)
  if (
    isLoading ||
    daysRemaining === null ||
    daysRemaining === undefined ||
    isNaN(daysRemaining) ||
    user?.is_superadmin === 1
  ) {
    return null; // CRITIQUE: Return null au lieu de laisser vide
  }

  // LOG FINAL avant affichage

  // Configuration du style selon les jours restants
  const getBadgeConfig = (days) => {
    // PROTECTION: Si days est null/undefined, retourner une config par défaut masquée
    if (days === null || days === undefined) {
      return {
        status: "default",
        color: "#d9d9d9",
        icon: <ClockCircleOutlined />,
        text: "Non disponible",
        urgencyClass: "trial-hidden",
        bgColor: "#f5f5f5",
        textColor: "#999999",
      };
    }

    if (days === 0) {
      return {
        status: "error",
        color: "#ff4d4f",
        icon: <ExclamationCircleOutlined />,
        text: "Expiré",
        urgencyClass: "trial-expired",
        bgColor: "#fff2f0",
        textColor: "#cf1322",
      };
    } else if (days <= 3) {
      return {
        status: "error",
        color: "#ff7875",
        icon: <FireOutlined />,
        text: `${days} jour${days > 1 ? "s" : ""} restant${
          days > 1 ? "s" : ""
        }`,
        urgencyClass: "trial-critical",
        bgColor: "#fff2f0",
        textColor: "#cf1322",
      };
    } else if (days <= 7) {
      return {
        status: "warning",
        color: "#faad14",
        icon: <WarningOutlined />,
        text: `${days} jours restants`,
        urgencyClass: "trial-warning",
        bgColor: "#fffbf0",
        textColor: "#d48806",
      };
    } else {
      return {
        status: "processing",
        color: "#173f5f",
        icon: <ClockCircleOutlined />,
        text: `${days} jours restants`,
        urgencyClass: "trial-normal",
        bgColor: "#f0f9ff",
        textColor: "#096dd9",
      };
    }
  };

  const config = getBadgeConfig(daysRemaining);

  const badgeContent = (
    <div
      className={`trial-countdown-badge ${config.urgencyClass} ${
        centered ? "trial-countdown-badge-center" : ""
      } ${className}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: size === "small" ? "4px 8px" : "6px 12px",
        borderRadius: "8px",
        backgroundColor: config.bgColor,
        border: `1px solid ${config.color}`,
        transition: "all 0.3s ease",
      }}
    >
      <Space
        size="small"
        style={{
          width: "100%",
          justifyContent: centered ? "center" : "flex-start",
        }}
      >
        <Badge
          status={config.status}
          style={{
            fontSize: size === "small" ? "12px" : "14px",
          }}
        />
        {config.icon}
        {showLabel && (
          <Text
            className="trial-label"
            style={{
              color: config.textColor,
              fontSize: size === "small" ? "12px" : "14px",
              fontWeight: daysRemaining <= 3 ? "bold" : "normal",
              whiteSpace: "nowrap",
            }}
          >
            Essai : {config.text}
          </Text>
        )}
      </Space>
    </div>
  );

  const tooltipTitle = (
    <div style={{ textAlign: "center" }}>
      <div style={{ marginBottom: "8px" }}>
        <Text strong style={{ color: "white" }}>
          Période d'essai ELSA GESTION
        </Text>
      </div>
      <div style={{ marginBottom: "8px" }}>
        {daysRemaining === 0 ? (
          <Text style={{ color: "#ff7875" }}>
            Votre essai a expiré le{" "}
            {new Date(
              user?.trial?.trialEndDate || user?.company?.trial_ends_at
            ).toLocaleDateString("fr-FR")}
          </Text>
        ) : (
          <>
            <Text style={{ color: "white" }}>
              Expire le :{" "}
              {new Date(
                user?.trial?.trialEndDate || user?.company?.trial_ends_at
              ).toLocaleDateString("fr-FR")}
            </Text>
            <br />
            <Text style={{ color: daysRemaining <= 3 ? "#ff7875" : "white" }}>
              {config.text}
            </Text>
          </>
        )}
      </div>
      {(daysRemaining <= 7 || daysRemaining === 0) && (
        <div
          style={{
            marginTop: "8px",
            paddingTop: "8px",
            borderTop: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <Text style={{ color: "white", fontSize: "12px" }}>
            Contactez notre équipe commerciale pour souscrire :
          </Text>
          <br />
          <Text style={{ color: "white", fontSize: "12px" }}>
            📞 +226 64 98 90 99
          </Text>
          <br />
          <Text style={{ color: "white", fontSize: "12px" }}>
            ✉️ info@elsa-technologies.com
          </Text>
        </div>
      )}
    </div>
  );

  const badge = (
    <Tooltip
      title={tooltipTitle}
      placement="bottomRight"
      overlayStyle={{ maxWidth: "300px" }}
    >
      {badgeContent}
    </Tooltip>
  );

  // Si bouton d'upgrade demandé et période critique
  if (onUpgradeClick && daysRemaining <= 7) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {badge}
        <Button
          type={daysRemaining <= 3 ? "primary" : "default"}
          size="small"
          onClick={onUpgradeClick}
          style={{
            borderColor: config.color,
            color: daysRemaining <= 3 ? "white" : config.textColor,
          }}
        >
          Souscrire
        </Button>
      </div>
    );
  }

  return badge;
};

// Hook personnalisé pour obtenir les infos d'essai
export const useTrialInfo = () => {
  const { user } = useAuth();

  const getTrialInfo = () => {
    // PROTECTION: Vérifier la validité de l'utilisateur et de l'entreprise
    if (!user || !user.company || user?.is_superadmin === 1) {
      return {
        isTrialActive: false,
        daysRemaining: null,
        isExpired: false,
        trialEndDate: null,
      };
    }

    // Si pas de trial_ends_at, pas de période d'essai active
    if (!user?.company?.trial_ends_at) {
      return {
        isTrialActive: false,
        daysRemaining: null,
        isExpired: false,
        trialEndDate: null,
      };
    }

    const company = user?.company;

    // VÉRIFIER SI L'ENTREPRISE A UN PLAN PAYANT ACTIF
    if (company) {
      // Si l'entreprise a un plan de souscription payant
      if (company.subscription_plan_id) {
        return {
          isTrialActive: false,
          daysRemaining: null,
          isExpired: false,
          trialEndDate: null,
          hasActiveSubscription: true,
        };
      }

      // Si l'entreprise a une licence valide (non expirée)
      if (company.licence_expire_on) {
        const licenceExpiry = new Date(company.licence_expire_on);
        const today = new Date();
        if (licenceExpiry > today) {
          return {
            isTrialActive: false,
            daysRemaining: null,
            isExpired: false,
            trialEndDate: null,
            hasActiveSubscription: true,
          };
        }
      }

      // Si l'entreprise a un statut actif et n'est plus en trial
      if (company.status === "active" && !company.trial_ends_at) {
        return {
          isTrialActive: false,
          daysRemaining: null,
          isExpired: false,
          trialEndDate: null,
          hasActiveSubscription: true,
        };
      }
    }

    const trialEndDate = new Date(user.company.trial_ends_at);
    const today = new Date();
    const diffTime = trialEndDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, diffDays);

    return {
      isTrialActive: true,
      daysRemaining,
      isExpired: daysRemaining === 0,
      trialEndDate,
      isCritical: daysRemaining <= 3,
      isWarning: daysRemaining <= 7,
      hasActiveSubscription: false,
    };
  };

  return getTrialInfo();
};

export default TrialCountdownBadge;
